import React, { useMemo, useState, useEffect } from "react";
import { useTutorOnboardingStore } from "src/store/tutorOnboardingStore";
import { TEACHING_EXPERIENCES } from "src/constants/teachingExperience";
import ScrollToTop from "src/components/ScrollToTop";

const TutorOnboardingStepThree: React.FC = () => {
  const { experience, setExperience } = useTutorOnboardingStore();
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Get currently selected experience roles
  const selectedExperienceRoles = experience.map(exp => exp.role);
  
  const getExperienceData = (role: string) => {
    return experience.find(exp => exp.role === role) || {
      role,
      years: 0,
      end_year: null,
      location: "",
      start_year: null,
      organization: ""
    };
  };
    
  // Check if a field has validation errors
  const hasError = (role: string, field: string): boolean => {
    if (role === "none") return false;
    const expData = getExperienceData(role);
    const fieldKey = `${role}_${field}`;
    
    if (!touched[fieldKey]) return false;
    
    switch (field) {
      case 'start_year':
        return !expData.start_year;
      case 'end_year':
        return !expData.end_year;
      case 'organization':
        return !expData.organization || !expData.organization.trim();
      default:
        return false;
    }
  };
  
  // Mark field as touched
  const markTouched = (role: string, field: string) => {
    const fieldKey = `${role}_${field}`;
    setTouched(prev => ({ ...prev, [fieldKey]: true }));
  };
  
  
  const toggleExperience = (experienceId: string) => {
    if (selectedExperienceRoles.includes(experienceId)) {
      // Remove this experience role
      setExperience(experience.filter(exp => exp.role !== experienceId));
    } else {
      // Add this experience role
      const newExperience = {
        role: experienceId,
        years: 0,
        end_year: null,
        location: "",
        start_year: null,
        organization: ""
      };
      setExperience([...experience, newExperience]);
    }
  };

  const updateExperienceField = (role: string, field: string, value: any) => {
    console.log('updateExperienceField called:', { role, field, value });
    console.log('Current experience before update:', experience);
    
    const updatedExperience = experience.map(exp => {
      if (exp.role === role) {
        const updated = { ...exp, [field]: value };
        console.log('Updating experience:', exp, 'to:', updated);
        return updated;
      }
      return exp;
    });
    
    console.log('Updated experience array:', updatedExperience);
    setExperience(updatedExperience);
  };

  
  // Calculate years for each experience - only when both start and end years are provided
  const calculateYears = (startYear: number | null, endYear: number | null): number => {
    if (!startYear) return 0;
    // Only calculate years if end year is explicitly provided
    if (endYear === null || endYear === undefined) return 0;
    return Math.max(0, endYear - startYear);
  };

  const experiences = TEACHING_EXPERIENCES;

  return (
    <>
      <ScrollToTop />
      <div className="w-full max-w-4xl mx-auto">
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Roboto, sans-serif', fontWeight: 450, fontSize: '1.8rem' }}>
            What teaching experience do you have?
          </h2>
          <p className="text-gray-600" style={{ fontFamily: 'Segoe UI, sans-serif' }}>
            Select all that apply to your background and provide details for each role.
          </p>
        </div>

        <div className="space-y-4">
          {experiences.map((experienceType) => {
            const isSelected = selectedExperienceRoles.includes(experienceType.id);
            const expData = getExperienceData(experienceType.id);
            const calculatedYears = calculateYears(expData.start_year, expData.end_year);

            // Determine disabling logic
            const isNoPriorExperience = experienceType.id === "none";
            const anyOtherSelected = selectedExperienceRoles.some(role => role !== "none");
            const isNoPriorSelected = selectedExperienceRoles.includes("none");
            const shouldDisable = isNoPriorExperience ? anyOtherSelected : isNoPriorSelected;

            // Special handling for "No Prior Experience"
            if (isNoPriorExperience) {
              return (
                <div
                  key={experienceType.id}
                  className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors duration-200"
                >
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleExperience(experienceType.id)}
                      className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                      disabled={shouldDisable}
                    />
                    <div className="font-semibold text-gray-800 text-lg" style={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400 }}>{experienceType.label}</div>
                  </label>
                </div>
              );
            }

            return (
              <div
                key={experienceType.id}
                className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors duration-200"
              >
                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleExperience(experienceType.id)}
                    className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                    disabled={shouldDisable}
                  />
                  <div className="flex-1">
                    <div 
                      className="font-semibold text-gray-800 mb-3 cursor-pointer text-lg"
                      style={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400 }}
                      onClick={() => toggleExperience(experienceType.id)}
                    >
                      {experienceType.label}
                    </div>
                    
                    {isSelected && (
                      <div>
                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Start Year <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="number"
                              placeholder="2020"
                              value={expData.start_year !== null ? expData.start_year : ""}
                              onChange={(e) => {
                                const value = e.target.value ? parseInt(e.target.value) : null;
                                // Update both start_year and years in a single state update
                                const years = calculateYears(value, expData.end_year);
                                const updatedExperience = experience.map(exp => 
                                  exp.role === experienceType.id 
                                    ? { ...exp, start_year: value, years: years }
                                    : exp
                                );
                                setExperience(updatedExperience);
                              }}
                              onBlur={() => markTouched(experienceType.id, 'start_year')}
                              readOnly={false}
                              disabled={shouldDisable}
                              className={`w-full px-3 py-2 border rounded-md focus:ring-2 text-sm ${
                                hasError(experienceType.id, 'start_year')
                                  ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                                  : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                              }`}
                            />
                            {hasError(experienceType.id, 'start_year') && (
                              <div className="text-sm text-red-600 mt-1">Start year is required</div>
                            )}
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              End Year <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="number"
                              placeholder="Present"
                              value={expData.end_year !== null ? expData.end_year : ""}
                              onChange={(e) => {
                                const value = e.target.value ? parseInt(e.target.value) : null;
                                // Update both end_year and years in a single state update
                                const years = calculateYears(expData.start_year, value);
                                const updatedExperience = experience.map(exp => 
                                  exp.role === experienceType.id 
                                    ? { ...exp, end_year: value, years: years }
                                    : exp
                                );
                                setExperience(updatedExperience);
                              }}
                              onBlur={() => markTouched(experienceType.id, 'end_year')}
                              readOnly={false}
                              disabled={shouldDisable}
                              className={`w-full px-3 py-2 border rounded-md focus:ring-2 text-sm ${
                                hasError(experienceType.id, 'end_year')
                                  ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                                  : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                              }`}
                            />
                            {hasError(experienceType.id, 'end_year') && (
                              <div className="text-sm text-red-600 mt-1">End year is required</div>
                            )}
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Years
                            </label>
                            <div className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-600 cursor-default">
                              {expData.start_year && expData.end_year 
                                ? `${calculatedYears} year${calculatedYears !== 1 ? 's' : ''}` 
                                : 'Enter start and end year'}
                            </div>
                          </div>
                          
                          <div className="md:col-span-2 lg:col-span-1">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Location
                            </label>
                            <input
                              type="text"
                              placeholder="New York, NY"
                              value={expData.location || ""}
                              onChange={(e) => {
                                updateExperienceField(experienceType.id, "location", e.target.value);
                              }}
                              disabled={shouldDisable}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                            />
                          </div>
                        </div>
                        
                        <div className="mt-3">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Organization <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            placeholder="University of Example"
                            value={expData.organization || ""}
                            onChange={(e) => {
                              updateExperienceField(experienceType.id, "organization", e.target.value);
                            }}
                            onBlur={() => markTouched(experienceType.id, 'organization')}
                            disabled={shouldDisable}
                            className={`w-full px-3 py-2 border rounded-md focus:ring-2 text-sm ${
                              hasError(experienceType.id, 'organization')
                                ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                                : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                            }`}
                          />
                          {hasError(experienceType.id, 'organization') && (
                            <div className="text-sm text-red-600 mt-1">Organization is required</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default TutorOnboardingStepThree;

